/* ESSNTL — FREE CAP WITH EVERY PIXEL SET

   One reconciler owns the free cap line for the whole store. Every surface
   (product page module, ESSNTL cart drawer, cart page, native Horizon drawer)
   reads from it and calls into it; none of them mutate the cart themselves.

   The cap line is identified by the `_essntl_free_cap` line property, never by
   variant id alone — so a cap the customer deliberately buys and pays for is
   left completely untouched.

   Configuration comes from snippets/essntl-free-cap.liquid. */

(function () {
  var config = window.ESSNTLFreeCapConfig || { enabled: false, promotions: [] };
  var promotions = (Array.isArray(config.promotions) ? config.promotions : []).filter(function (promo) {
    return promo && Array.isArray(promo.colours) && promo.colours.length > 0;
  });

  if (!config.enabled || promotions.length === 0) {
    window.ESSNTLFreeCap = {
      enabled: false,
      reconcile: function (cart) {
        return Promise.resolve(cart);
      },
      isCapLine: function () {
        return false;
      },
    };
    return;
  }

  var STORAGE_KEY = 'essntl_free_cap_colour';
  var PROPERTY = '_essntl_free_cap';
  var PROPERTY_COLOUR = '_essntl_free_cap_colour';
  var PROPERTY_PROMO = '_essntl_free_cap_promo';
  var PROPERTY_NOTE = '_essntl_free_cap_note';
  var EVENT_COOLDOWN_MS = 300;

  /* The order-wide ceiling. Clamped to at least 1 here as well as in the
     config, so no setting can ever produce a cart with two free caps. */
  var maxFreeCaps = Math.max(1, parseInt(config.maxFreeCaps, 10) || 1);
  /* Every cap variant across every promotion, so a line can be traced back to
     the promotion that owns it. */
  var capVariantIds = {};
  promotions.forEach(function (promo) {
    promo.match = promo.match || {};
    promo.terms = (promo.match.terms || []).map(function (term) {
      return String(term || '').trim().toLowerCase();
    }).filter(Boolean);
    promo.colours.forEach(function (colour) {
      capVariantIds[String(colour.variantId)] = { colour: colour, promo: promo };
    });
  });

  /* Mirrors snippets/essntl-product-family.liquid. Pixel Shell before Pixel Set,
     so the shared word "Pixel" can never group them. */
  function familyOf(item) {
    var signal = [item && item.handle, item && item.product_title, item && item.title,
                  item && item.product_type].join(' ').toLowerCase();
    if (signal.indexOf('pixel shell') !== -1 || signal.indexOf('pixel-shell') !== -1) return 'pixel_shell';
    if (signal.indexOf('pixel set') !== -1 || signal.indexOf('pixel-set') !== -1) return 'pixel_set';
    if (signal.indexOf('tracksuit') !== -1 || signal.indexOf('track suit') !== -1) return 'tracksuit';
    if (signal.indexOf('zip hoodie') !== -1 || signal.indexOf('zip-hoodie') !== -1) return 'zip_hoodie';
    if (signal.indexOf('hoodie') !== -1) return 'hoodie';
    if (signal.indexOf('sweat') !== -1) return 'sweats';
    if (signal.indexOf('shorts') !== -1) return 'shorts';
    return '';
  }

  /* Mirrors snippets/essntl-product-line.liquid: only the exact
     "ESSNTL <COLOUR> <GARMENT>" shape is a signature colourway. */
  function lineOf(item) {
    var title = String((item && item.product_title) || (item && item.title) || '').toLowerCase().trim();
    if (title.indexOf(' in ') !== -1) return 'classic';
    var words = title.split(/\s+/);
    var garments = ['tracksuit', 'hoodie', 'sweatpants', 'sweats', 'shorts', 'sweatshirt'];
    if (words.length >= 3 && words[0] === 'essntl' && garments.indexOf(words[words.length - 1]) !== -1) {
      return 'signature';
    }
    return 'classic';
  }

  /* Which promotion, if any, a cart line qualifies for. Explicit on purpose:
     a term list OR an exact family+line pair, never a loose word match. */
  function promotionFor(item) {
    if (!item || isCapLine(item)) return null;
    if (capVariantIds[String(item.variant_id)]) return null;

    var signal = [item.handle, item.product_title, item.title, item.product_type]
      .join(' ')
      .toLowerCase();

    for (var i = 0; i < promotions.length; i += 1) {
      var promo = promotions[i];
      var match = promo.match || {};

      if (match.family) {
        if (familyOf(item) !== match.family) continue;
        if (match.line && lineOf(item) !== match.line) continue;
        return promo;
      }

      for (var t = 0; t < promo.terms.length; t += 1) {
        if (signal.indexOf(promo.terms[t]) !== -1) return promo;
      }
    }
    return null;
  }

  function promoById(id) {
    for (var i = 0; i < promotions.length; i += 1) {
      if (promotions[i].id === id) return promotions[i];
    }
    return null;
  }

  var busy = false;
  var queued = false;
  var inFlight = null;
  var ignoreEventsUntil = 0;
  var selfDispatching = false;

  /* ── helpers ───────────────────────────────────────────────── */

  function route(path) {
    var root = config.rootUrl || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + String(path || '').replace(/^\//, '');
  }

  function postJSON(path, body) {
    return fetch(route(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      if (!response.ok) throw new Error('[ESSNTL free cap] ' + path + ' failed (' + response.status + ')');
      return response.json();
    });
  }

  function getCart() {
    return fetch(route('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      if (!response.ok) throw new Error('[ESSNTL free cap] could not load the cart');
      return response.json();
    });
  }

  function escapeHTML(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* Mirrors Shopify's money filter for the handful of formats a UK/EU store
     actually uses. Only needed for the honest fallback below — when the cap is
     genuinely free we never print a number. */
  function formatMoney(cents) {
    var amount = (parseInt(cents, 10) || 0) / 100;
    var format = config.moneyFormat || '\u00A3{{amount}}';

    function withSeparators(decimals, thousands, decimalMark) {
      var fixed = amount.toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimalMark + parts[1] : whole;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount':
          return withSeparators(2, ',', '.');
        case 'amount_no_decimals':
          return withSeparators(0, ',', '.');
        case 'amount_with_comma_separator':
          return withSeparators(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withSeparators(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withSeparators(2, "'", '.');
        case 'amount_with_space_separator':
          return withSeparators(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withSeparators(0, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return withSeparators(2, ' ', '.');
        default:
          return withSeparators(2, ',', '.');
      }
    });
  }

  function colourByKey(promo, key) {
    if (!promo) return null;
    for (var i = 0; i < promo.colours.length; i += 1) {
      if (promo.colours[i].key === key) return promo.colours[i];
    }
    return null;
  }

  function capEntryByVariantId(variantId) {
    return capVariantIds[String(variantId)] || null;
  }

  function colourByVariantId(variantId) {
    var entry = capEntryByVariantId(variantId);
    return entry ? entry.colour : null;
  }

  function firstAvailableColour(promo) {
    if (!promo) return null;
    for (var i = 0; i < promo.colours.length; i += 1) {
      if (promo.colours[i].available) return promo.colours[i];
    }
    return null;
  }

  /* The default this page asks for, read from the module. Declared before the
     storage helpers because the remembered choice is scoped by it. */
  function pageDefaultKey(promo) {
    if (!promo || !promo.id) return '';
    var root = document.querySelector(
      '[data-essntl-free-cap][data-fc-promotion="' + promo.id + '"][data-fc-default]'
    );
    if (!root) return '';
    return (root.getAttribute('data-fc-default') || '').trim();
  }

  /* One remembered choice per promotion AND per colourway context, so a Pixel
     Set colour never leaks into another promotion's cap and — just as
     importantly — a Black chosen on Midnight is not read back as the choice for
     Moonlight. Midnight and Moonlight ask for different defaults, so they get
     different buckets: an explicit pick is remembered for the product it was
     made on, while a product with no pick of its own falls through to its own
     matching default instead of inheriting the other colourway's. */
  function storageKeyFor(promo) {
    var base = STORAGE_KEY + (promo && promo.id ? '_' + promo.id : '');
    var ctx = pageDefaultKey(promo);
    return ctx ? base + ':' + ctx : base;
  }

  function readStoredColour(promo) {
    try {
      return window.localStorage.getItem(storageKeyFor(promo));
    } catch (error) {
      return null;
    }
  }

  function writeStoredColour(promo, key) {
    try {
      window.localStorage.setItem(storageKeyFor(promo), key);
    } catch (error) {
      /* Private mode: the default colour still applies, it just is not remembered. */
    }
  }

  /* The colour this page asks for when the customer has not chosen one: the
     beanie matching the Tracksuit's own colourway, published by the module as
     data-fc-default. It is deliberately NOT written to storage — an automatic
     default must never look like an explicit choice, so moving from Midnight to
     Moonlight re-defaults to Grey instead of carrying Black across. */
  function pageDefaultColour(promo) {
    var key = pageDefaultKey(promo);
    return key ? colourByKey(promo, key) : null;
  }

  /* The colour we will actually put in the cart: the customer's choice when it
     can be sold, then this page's matching default, otherwise the first colour
     that can be sold. */
  function activeColour(promo) {
    if (!promo) return null;
    var stored = colourByKey(promo, readStoredColour(promo));
    if (stored && stored.available) return stored;

    var matching = pageDefaultColour(promo);
    if (matching && matching.available) return matching;

    var fallback = colourByKey(promo, promo.defaultColour || '');
    if (fallback && fallback.available) return fallback;

    return firstAvailableColour(promo);
  }

  /* The colour we show as selected. A stored choice that has since sold out
     falls back, so the chips never disagree with what is in the cart. */
  function displayColour(promo) {
    promo = promo || currentPromotion();
    if (!promo) return null;
    var stored = colourByKey(promo, readStoredColour(promo));
    if (stored && stored.available) return stored;
    return activeColour(promo) || stored || promo.colours[0];
  }

  /* The promotion in play right now:
       1. the module rendered on this product page, if there is one
       2. otherwise the promotion that owns the gift already in the cart, so a
          colour switch on the cart page targets the right promotion
       3. otherwise the first configured promotion */
  function currentPromotion() {
    var root = document.querySelector('[data-essntl-free-cap][data-fc-promotion]');
    if (root) {
      var found = promoById(root.getAttribute('data-fc-promotion'));
      if (found) return found;
    }

    var cart = window.ESSNTL_LAST_CART;
    if (cart && cart.items) {
      for (var i = 0; i < cart.items.length; i += 1) {
        if (!isCapLine(cart.items[i])) continue;
        var owner = promoOfCapLine(cart.items[i]);
        if (owner) return owner;
      }
    }

    return promotions[0] || null;
  }

  /* ── cart classification ───────────────────────────────────── */

  function isCapLine(item) {
    if (!item || !item.properties) return false;
    var flag = item.properties[PROPERTY];
    if (flag !== 'true' && flag !== true) return false;
    return Boolean(colourByVariantId(item.variant_id));
  }

  function isEligibleLine(item) {
    return Boolean(promotionFor(item));
  }

  function eligibleUnits(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (total, item) {
      return isEligibleLine(item) ? total + (parseInt(item.quantity, 10) || 0) : total;
    }, 0);
  }

  /* Promotions with at least one qualifying product in the cart, in config
     order. Config order is the tie-break, so the established promotion wins
     when nothing is in the bag yet. */
  function activePromotions(cart) {
    var seen = {};
    var out = [];
    if (!cart || !cart.items) return out;
    cart.items.forEach(function (item) {
      var promo = promotionFor(item);
      if (promo && !seen[promo.id]) {
        seen[promo.id] = true;
      }
    });
    promotions.forEach(function (promo) {
      if (seen[promo.id]) out.push(promo);
    });
    return out;
  }

  function promoOfCapLine(item) {
    if (!item || !item.properties) return null;
    var byProp = promoById(item.properties[PROPERTY_PROMO]);
    if (byProp) return byProp;
    var entry = capEntryByVariantId(item.variant_id);
    return entry ? entry.promo : null;
  }

  function capLines(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(isCapLine);
  }

  /* One free cap for the whole order. Not one per Pixel Set, and not one per
     line: the quantity of eligible sets decides only whether a cap is owed at
     all, never how many. */
  /* Which promotion should be honoured for this cart, and with which colour.
     One free cap for the entire order: the winner is whichever promotion
     already has a cap in the bag (so nothing swaps under the customer), and
     otherwise the first active promotion in config order. */
  function resolveAward(cart) {
    var active = activePromotions(cart);
    if (active.length === 0) return null;

    var existing = capLines(cart);
    for (var i = 0; i < existing.length; i += 1) {
      var owner = promoOfCapLine(existing[i]);
      if (!owner) continue;
      for (var a = 0; a < active.length; a += 1) {
        if (active[a].id === owner.id) {
          var keepColour = activeColour(owner);
          if (keepColour) return { promo: owner, colour: keepColour };
        }
      }
    }

    for (var p = 0; p < active.length; p += 1) {
      var colour = activeColour(active[p]);
      if (colour) return { promo: active[p], colour: colour };
    }
    return null;
  }

  function desiredQuantity(cart) {
    return resolveAward(cart) ? maxFreeCaps : 0;
  }

  /* ── reconcile ─────────────────────────────────────────────── */

  function removeLines(lines) {
    var updates = {};
    lines.forEach(function (line) {
      if (line.key) updates[line.key] = 0;
    });
    if (Object.keys(updates).length === 0) return null;
    return postJSON('cart/update.js', { updates: updates });
  }

  function runReconcile(seedCart) {
    var mutated = false;

    return Promise.resolve(seedCart && seedCart.items ? seedCart : getCart())
      .then(function (cart) {
        var lines = capLines(cart);
        var wanted = desiredQuantity(cart);

        if (wanted === 0) {
          if (lines.length === 0) return cart;
          mutated = true;
          return removeLines(lines) || cart;
        }

        var award = resolveAward(cart);
        var colour = award.colour;
        var keep = null;
        var stale = [];

        lines.forEach(function (line) {
          if (!keep && Number(line.variant_id) === Number(colour.variantId)) {
            keep = line;
          } else {
            stale.push(line);
          }
        });

        var chain = Promise.resolve(cart);

        if (stale.length) {
          mutated = true;
          chain = chain.then(function () {
            return removeLines(stale);
          });
        }

        if (keep) {
          if (parseInt(keep.quantity, 10) !== wanted) {
            mutated = true;
            chain = chain.then(function () {
              return postJSON('cart/change.js', { id: keep.key, quantity: wanted });
            });
          }
        } else {
          mutated = true;
          var properties = {};
          properties[PROPERTY] = 'true';
          properties[PROPERTY_COLOUR] = colour.label;
          properties[PROPERTY_PROMO] = award.promo.id;
          properties[PROPERTY_NOTE] = (award.promo.strings && award.promo.strings.inCart) || '';

          chain = chain.then(function () {
            return postJSON('cart/add.js', {
              items: [{ id: colour.variantId, quantity: wanted, properties: properties }],
            });
          });
        }

        return chain.then(function () {
          return mutated ? getCart() : cart;
        });
      })
      .then(function (cart) {
        /* A cap already in the cart is the source of truth for the chip state
           when the customer comes back in a new session. */
        var existing = capLines(cart)[0];
        if (existing) {
          var owner = promoOfCapLine(existing);
          var existingColour = colourByVariantId(existing.variant_id);
          if (owner && existingColour && !readStoredColour(owner)) {
            writeStoredColour(owner, existingColour.key);
          }
        }

        window.ESSNTL_LAST_CART = cart;
        ignoreEventsUntil = Date.now() + EVENT_COOLDOWN_MS;

        if (mutated) {
          selfDispatching = true;
          try {
            document.dispatchEvent(
              new CustomEvent('essntl:free-cap:updated', { detail: { cart: cart } })
            );

            /* Both ESSNTL cart drawers already redraw themselves on this event —
               it is what the cart variant editor uses. Emitting it here is what
               makes a colour swap visible: without it the cart really did change
               server side, but nothing on screen moved. */
            document.dispatchEvent(
              new CustomEvent('essntl:cart:variant-updated', {
                bubbles: true,
                detail: { cart: cart, source: 'essntl-free-cap' },
              })
            );
          } finally {
            selfDispatching = false;
          }
        }

        return { cart: cart, mutated: mutated };
      });
  }

  function reconcile(seedCart) {
    if (busy) {
      queued = true;
      return inFlight || Promise.resolve(seedCart);
    }

    busy = true;
    inFlight = runReconcile(seedCart)
      .catch(function (error) {
        console.error('[ESSNTL free cap]', error);
        return { cart: seedCart || window.ESSNTL_LAST_CART || null, mutated: false };
      })
      .then(function (result) {
        busy = false;
        inFlight = null;

        if (queued) {
          queued = false;
          return reconcile(result.cart).then(function (cart) {
            return cart;
          });
        }

        if (result.mutated) refreshCartPageSection();
        return result.cart;
      });

    return inFlight;
  }

  /* Cart page only: the rows are server rendered, so re-render the section
     after a mutation instead of patching the DOM by hand. */
  function refreshCartPageSection() {
    var host = document.querySelector('cart-items-component[data-section-id]:not([data-drawer])');
    if (!host) return;

    var sectionId = host.getAttribute('data-section-id');
    if (!sectionId || sectionId === 'cart-drawer-section') return;

    import('@theme/section-renderer')
      .then(function (module) {
        return module.sectionRenderer.renderSection(sectionId, { cache: false, mode: 'full' });
      })
      .then(hydrateCartChips)
      .catch(function () {
        /* Section Rendering unavailable — the next navigation shows the change. */
      });
  }

  /* ── colour selection ──────────────────────────────────────── */

  function setColour(key, options) {
    var promo = (options && options.promotion) || currentPromotion();
    var colour = colourByKey(promo, key);
    if (!colour) return Promise.resolve(window.ESSNTL_LAST_CART || null);

    writeStoredColour(promo, colour.key);
    syncModules();

    var force = options && options.force;
    var cart = window.ESSNTL_LAST_CART;
    var hasLine = cart ? capLines(cart).length > 0 : false;

    /* Before anything is in the cart the choice is just stored — no request.
       Once the cap exists as a line, swapping colour has to hit the cart. */
    if (!force && !hasLine) return Promise.resolve(cart);

    return reconcile();
  }

  /* Keeps every product-page module on the page in sync with the stored choice. */
  function syncModules() {
    document.querySelectorAll('[data-essntl-free-cap]').forEach(function (root) {
      var current = displayColour(promoById(root.getAttribute('data-fc-promotion')));
      if (!current) return;
      if (root.getAttribute('data-fc-selected') === current.key) return;

      root.setAttribute('data-fc-selected', current.key);

      var chips = root.querySelectorAll('[data-fc-chip]');
      chips.forEach(function (chip) {
        var on = chip.getAttribute('data-fc-chip') === current.key;
        chip.classList.toggle('is-selected', on);
        chip.setAttribute('aria-checked', on ? 'true' : 'false');
        chip.setAttribute('tabindex', on ? '0' : '-1');
      });

      var label = root.querySelector('[data-fc-colour-label]');
      if (label) label.textContent = current.label;

      /* The struck reference value only exists for colours that have one. A
         gift with no compare-at price shows a bare FREE, and switching between
         the two cases has to add or hide the element rather than assume it. */
      var was = root.querySelector('[data-fc-value]');
      if (current.valueFormatted) {
        if (!was) {
          var priceBox = root.querySelector('[data-fc-price]') || root.querySelector('.essntl-free-cap__price');
          if (priceBox) {
            was = document.createElement('s');
            was.className = 'essntl-free-cap__was';
            was.setAttribute('data-fc-value', '');
            priceBox.insertBefore(was, priceBox.firstChild);
          }
        }
        if (was) {
          was.textContent = current.valueFormatted;
          was.hidden = false;
        }
      } else if (was) {
        was.hidden = true;
      }

      var image = root.querySelector('[data-fc-image]');
      if (image && current.image) {
        root.classList.add('is-swapping');
        image.removeAttribute('srcset');
        image.setAttribute('src', current.image);
        image.setAttribute('alt', (config.displayName || 'Cap') + ' \u2014 ' + current.label);
        window.setTimeout(function () {
          root.classList.remove('is-swapping');
        }, 160);
      }

      var live = root.querySelector('[data-fc-live]');
      if (live) live.textContent = 'Free cap colour: ' + current.label;
    });
  }

  /* ── markup shared by the cart drawer and the cart page ────── */

  function chipButtonsHTML(selectedKey, promo) {
    promo = promo || currentPromotion();
    if (!promo) return '';
    return (
      promo.colours
        .map(function (colour) {
          var on = colour.key === selectedKey;
          return (
            '<button type="button" class="essntl-fc-chip' +
            (on ? ' is-selected' : '') +
            '" data-fc-cart-chip="' +
            escapeHTML(colour.key) +
            '" role="radio" aria-checked="' +
            (on ? 'true' : 'false') +
            '"' +
            (colour.available ? '' : ' disabled aria-disabled="true"') +
            '><span class="essntl-fc-chip__dot" style="--fc-dot: ' +
            escapeHTML(colour.swatch) +
            ';" aria-hidden="true"></span>' +
            escapeHTML(colour.label) +
            (colour.available ? '' : ' \u2014 ' + escapeHTML(config.strings.soldOut)) +
            '</button>'
          );
        })
        .join('')
    );
  }

  function chipsHTML(selectedKey, promo) {
    var buttons = chipButtonsHTML(selectedKey, promo);
    if (!buttons) return '';
    return (
      '<div class="essntl-fc-line__chips" role="radiogroup" aria-label="Choose your free cap colour">' +
      buttons +
      '</div>'
    );
  }

  /* The cart page renders its rows server side and cannot know the colour list,
     so it leaves a height-reserved placeholder for us to fill. Idempotent, and
     re-run whenever the cart section re-renders. */
  function hydrateCartChips() {
    document.querySelectorAll('[data-fc-cart-chips]').forEach(function (host) {
      var wanted = String(host.getAttribute('data-fc-line-colour') || '').trim().toLowerCase();
      var promo = promoById(host.getAttribute('data-fc-line-promo')) || currentPromotion();
      var colour = null;

      if (promo) {
        for (var i = 0; i < promo.colours.length; i += 1) {
          var candidate = promo.colours[i];
          if (candidate.key === wanted || String(candidate.label).toLowerCase() === wanted) {
            colour = candidate;
            break;
          }
        }
      }

      if (!colour) colour = displayColour(promo);
      var key = colour ? colour.key : '';
      if (host.getAttribute('data-fc-hydrated') === key) return;

      host.innerHTML = chipButtonsHTML(key, promo);
      host.setAttribute('data-fc-hydrated', key);
    });
  }

  function watchCartRows() {
    var scheduled = false;

    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        hydrateCartChips();
      });
    });

    document.querySelectorAll('cart-items-component').forEach(function (host) {
      observer.observe(host, { childList: true, subtree: true });
    });
  }

  function lineColour(item) {
    return colourByVariantId(item && item.variant_id) || displayColour(promoOfCapLine(item));
  }

  function detailsHTML(item) {
    var colour = lineColour(item);
    var quantity = parseInt(item && item.quantity, 10) || 1;

    var promo = promoOfCapLine(item) || currentPromotion();
    var note = (item && item.properties && item.properties[PROPERTY_NOTE])
      || (promo && promo.strings && promo.strings.inCart)
      || 'Free with your order';

    return (
      '<div class="essntl-fc-line">' +
      '<span class="essntl-fc-line__note">' +
      escapeHTML(note) +
      (quantity > 1 ? ' \u00B7 \u00D7' + quantity : '') +
      '</span>' +
      chipsHTML(colour ? colour.key : '', promo) +
      '</div>'
    );
  }

  function priceHTML(item) {
    var colour = lineColour(item);
    var quantity = parseInt(item && item.quantity, 10) || 1;
    var charged = Number(item && item.final_line_price);
    var value = colour ? colour.valueFormatted : '';

    /* Only ever say FREE when the line really is free. Gift products are
       priced at 0, so this is normally true; if anything ever does charge for
       the line, show what is being charged rather than a promise the cart is
       not keeping. */
    if (charged > 0) {
      return '<span class="essntl-fc-price">' + escapeHTML(formatMoney(charged)) + '</span>';
    }

    return (
      '<span class="essntl-fc-price">' +
      (value ? '<s>' + escapeHTML(value) + (quantity > 1 ? ' \u00D7 ' + quantity : '') + '</s>' : '') +
      '<span class="essntl-fc-price__free">' +
      escapeHTML(config.strings.free) +
      '</span></span>'
    );
  }


  /* ── cap image viewer ──────────────────────────────────────────
     A near-fullscreen gallery for the cap the customer is being given, so the
     free gift can be inspected like any other product rather than taken on
     trust from a 52px thumbnail.

     Swiping is native scroll-snap rather than a gesture library: it matches the
     platform's own inertia, costs nothing, and cannot fight the page. The
     <dialog> gives modal focus containment, Escape, and background inertness
     for free. Slides are rebuilt on every open, so the gallery always matches
     the colour that is selected right now. */

  var viewer = null;
  var viewerIndex = 0;
  var viewerCount = 0;
  var viewerTrigger = null;
  var scrollLock = null;

  function lockPageScroll() {
    /* A lock left over from a previous open would make this a no-op and leak
       the wrong values, so always start from a clean slate. */
    if (scrollLock) unlockPageScroll();

    var root = document.documentElement;
    var gutter = window.innerWidth - root.clientWidth;

    scrollLock = {
      rootOverflow: root.style.overflow,
      bodyOverflow: document.body.style.overflow,
      bodyPadding: document.body.style.paddingRight,
    };

    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    /* Replace the scrollbar's width so the page behind does not shift sideways
       as it disappears. */
    if (gutter > 0) document.body.style.paddingRight = gutter + 'px';
  }

  function unlockPageScroll() {
    if (!scrollLock) return;
    document.documentElement.style.overflow = scrollLock.rootOverflow;
    document.body.style.overflow = scrollLock.bodyOverflow;
    document.body.style.paddingRight = scrollLock.bodyPadding;
    scrollLock = null;
  }

  function buildViewer() {
    if (viewer) return viewer;

    var dialog = document.createElement('dialog');
    dialog.className = 'essntl-fc-viewer';
    dialog.setAttribute('aria-label', (config.displayName || 'Cap') + ' images');
    dialog.innerHTML = [
      '<div class="essntl-fc-viewer__inner">',
      '  <button type="button" class="essntl-fc-viewer__close" data-fc-viewer-close aria-label="Close">',
      '    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">',
      '      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
      '    </svg>',
      '  </button>',
      '  <button type="button" class="essntl-fc-viewer__arrow essntl-fc-viewer__arrow--prev" data-fc-viewer-prev aria-label="Previous image">',
      '    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">',
      '      <path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
      '    </svg>',
      '  </button>',
      '  <div class="essntl-fc-viewer__track" data-fc-viewer-track tabindex="0"></div>',
      '  <button type="button" class="essntl-fc-viewer__arrow essntl-fc-viewer__arrow--next" data-fc-viewer-next aria-label="Next image">',
      '    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">',
      '      <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
      '    </svg>',
      '  </button>',
      '  <div class="essntl-fc-viewer__footer">',
      '    <p class="essntl-fc-viewer__caption" data-fc-viewer-caption></p>',
      '    <div class="essntl-fc-viewer__dots" data-fc-viewer-dots></div>',
      '  </div>',
      '</div>',
    ].join('');

    document.body.appendChild(dialog);

    viewer = {
      dialog: dialog,
      track: dialog.querySelector('[data-fc-viewer-track]'),
      dots: dialog.querySelector('[data-fc-viewer-dots]'),
      caption: dialog.querySelector('[data-fc-viewer-caption]'),
      prev: dialog.querySelector('[data-fc-viewer-prev]'),
      next: dialog.querySelector('[data-fc-viewer-next]'),
    };

    /* Which slide is showing is read two independent ways: an
       IntersectionObserver (the reliable one — it is driven by the compositor,
       not by scroll events) and a scroll listener (cheap, and keeps the counter
       moving during the swipe). Either alone is enough; both agree. */
    if (typeof IntersectionObserver === 'function') {
      viewer.observer = new IntersectionObserver(
        function (entries) {
          var best = null;
          for (var i = 0; i < entries.length; i += 1) {
            if (!best || entries[i].intersectionRatio > best.intersectionRatio) best = entries[i];
          }
          if (!best || best.intersectionRatio < 0.55) return;

          var index = Array.prototype.indexOf.call(viewer.track.children, best.target);
          if (index < 0 || index === viewerIndex) return;

          viewerIndex = index;
          renderViewerChrome();
        },
        { root: viewer.track, threshold: [0.55, 0.8, 1] }
      );
    }

    var ticking = false;
    viewer.track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          syncViewerIndex();
        });
      },
      { passive: true }
    );

    /* Teardown never hangs off a single event. The page scroll lock is the one
       piece of state that must not survive the dialog — leaving it stuck would
       freeze the product page — so it is released by whichever of these fires
       first, and releasing twice is a no-op. */
    dialog.addEventListener('close', afterViewerClosed);
    dialog.addEventListener('cancel', afterViewerClosed);

    /* The backstop: whatever closes the dialog, native or not, the `open`
       attribute goes away and this runs. */
    new MutationObserver(function () {
      if (!dialog.open) afterViewerClosed();
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

    /* Tapping the space around the photo closes, the way a native gallery does.
       Swipes end as scroll events, not clicks, so this cannot fire mid-swipe. */
    dialog.addEventListener('click', function (event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('button') || target.closest('img')) return;
      closeViewer();
    });

    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeViewer();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToImage(viewerIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToImage(viewerIndex + 1);
      }
    });

    return viewer;
  }

  function afterViewerClosed() {
    unlockPageScroll();

    if (viewer && viewer.observer) viewer.observer.disconnect();

    if (viewerTrigger && document.contains(viewerTrigger)) {
      viewerTrigger.focus({ preventScroll: true });
    }
    viewerTrigger = null;
  }

  function closeViewer() {
    if (!viewer) return;
    if (viewer.dialog.open) viewer.dialog.close();
    afterViewerClosed();
  }

  function syncViewerIndex() {
    if (!viewer || viewerCount === 0) return;

    var width = viewer.track.clientWidth || 1;
    var next = Math.round(viewer.track.scrollLeft / width);
    next = Math.max(0, Math.min(viewerCount - 1, next));
    if (next === viewerIndex) return;

    viewerIndex = next;
    renderViewerChrome();
  }

  function renderViewerChrome() {
    if (!viewer) return;

    var promo = currentPromotion();
    var colour = displayColour(promo);
    var name = (promo && promo.displayName) || 'Cap';
    var label = colour ? colour.label : '';
    var position = viewerCount > 1 ? ' \u00B7 ' + (viewerIndex + 1) + ' / ' + viewerCount : '';

    viewer.caption.textContent = name + (label ? ' \u2014 ' + label : '') + position;

    var dots = viewer.dots.children;
    for (var i = 0; i < dots.length; i += 1) {
      var on = i === viewerIndex;
      dots[i].classList.toggle('is-active', on);
      dots[i].setAttribute('aria-current', on ? 'true' : 'false');
    }

    viewer.prev.disabled = viewerIndex <= 0;
    viewer.next.disabled = viewerIndex >= viewerCount - 1;
  }

  function goToImage(index) {
    if (!viewer || viewerCount === 0) return;

    var track = viewer.track;
    var target = Math.max(0, Math.min(viewerCount - 1, index));
    var width = track.clientWidth || 1;
    var left = target * width;
    var origin = track.scrollLeft;
    var behavior = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';

    try {
      track.scrollTo({ left: left, behavior: behavior });
    } catch (error) {
      track.scrollLeft = left;
    }

    /* Some engines quietly ignore smooth scrolling. If nothing has moved at all
       by the time the animation should have finished, jump. The `=== origin`
       test means a customer who started swiping mid-animation is never yanked
       back. */
    window.setTimeout(function () {
      if (track.scrollLeft === origin && Math.abs(origin - left) > 2) {
        track.scrollLeft = left;
      }
    }, 420);

    viewerIndex = target;
    renderViewerChrome();
  }

  function currentImages() {
    var colour = displayColour(currentPromotion());
    return (colour && Array.isArray(colour.images) ? colour.images : []).filter(function (image) {
      return image && image.src;
    });
  }

  function openViewer(trigger) {
    var images = currentImages();
    if (!images.length) return;

    var instance = buildViewer();
    var colour = displayColour(currentPromotion());
    var fallbackAlt = (config.displayName || 'Cap') + (colour ? ' \u2014 ' + colour.label : '');

    viewerTrigger = trigger || null;
    viewerIndex = 0;
    viewerCount = images.length;

    /* Slides are only built when the customer actually opens the gallery, so
       nothing here is downloaded up front. Once open, every image loads
       immediately — lazy loading inside a horizontal scroller shows a blank
       frame at the exact moment the customer swipes to it. */
    instance.track.innerHTML = images
      .map(function (image) {
        return (
          '<div class="essntl-fc-viewer__slide">' +
          '<img src="' + escapeHTML(image.src) + '"' +
          ' alt="' + escapeHTML(image.alt || fallbackAlt) + '"' +
          (image.width ? ' width="' + escapeHTML(image.width) + '"' : '') +
          (image.height ? ' height="' + escapeHTML(image.height) + '"' : '') +
          ' decoding="async"' +
          '>' +
          '</div>'
        );
      })
      .join('');

    instance.dots.innerHTML = images
      .map(function (_image, index) {
        return (
          '<button type="button" class="essntl-fc-viewer__dot" data-fc-viewer-dot="' +
          index +
          '" aria-label="Image ' + (index + 1) + '"></button>'
        );
      })
      .join('');

    instance.dialog.classList.toggle('is-single', viewerCount < 2);
    instance.track.scrollLeft = 0;

    if (instance.observer) {
      instance.observer.disconnect();
      Array.prototype.forEach.call(instance.track.children, function (slide) {
        instance.observer.observe(slide);
      });
    }

    lockPageScroll();
    if (typeof instance.dialog.showModal === 'function') {
      instance.dialog.showModal();
    } else {
      instance.dialog.setAttribute('open', '');
    }

    renderViewerChrome();
  }

  /* ── event wiring ──────────────────────────────────────────── */

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    var openTrigger = target.closest('[data-fc-open-viewer]');
    if (openTrigger) {
      event.preventDefault();
      openViewer(openTrigger);
      return;
    }

    if (viewer) {
      if (target.closest('[data-fc-viewer-close]')) {
        event.preventDefault();
        closeViewer();
        return;
      }
      if (target.closest('[data-fc-viewer-prev]')) {
        event.preventDefault();
        goToImage(viewerIndex - 1);
        return;
      }
      if (target.closest('[data-fc-viewer-next]')) {
        event.preventDefault();
        goToImage(viewerIndex + 1);
        return;
      }
      var dot = target.closest('[data-fc-viewer-dot]');
      if (dot) {
        event.preventDefault();
        goToImage(parseInt(dot.getAttribute('data-fc-viewer-dot'), 10) || 0);
        return;
      }
    }

    var moduleChip = target.closest('[data-fc-chip]');
    if (moduleChip && !moduleChip.disabled) {
      event.preventDefault();
      var moduleRoot = moduleChip.closest('[data-essntl-free-cap]');
      setColour(moduleChip.getAttribute('data-fc-chip'), {
        promotion: moduleRoot ? promoById(moduleRoot.getAttribute('data-fc-promotion')) : null,
      });
      return;
    }

    var cartChip = target.closest('[data-fc-cart-chip]');
    if (cartChip && !cartChip.disabled) {
      event.preventDefault();
      var row = cartChip.closest('[data-cart-drawer-item], [data-essntl-free-cap]');
      if (row) row.setAttribute('aria-busy', 'true');
      var chipHost = cartChip.closest('[data-fc-cart-chips]');
      setColour(cartChip.getAttribute('data-fc-cart-chip'), {
        force: true,
        promotion: chipHost ? promoById(chipHost.getAttribute('data-fc-line-promo')) : null,
      }).then(function () {
        if (row) row.removeAttribute('aria-busy');
      });
    }
  });

  /* Arrow keys move between colours, as expected inside a radiogroup. */
  document.addEventListener('keydown', function (event) {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(event.key) === -1) return;

    var target = event.target instanceof Element ? event.target : null;
    var chip = target && target.closest('[data-fc-chip], [data-fc-cart-chip]');
    if (!chip) return;

    var group = chip.parentElement;
    if (!group) return;

    var buttons = Array.prototype.filter.call(
      group.querySelectorAll('[data-fc-chip], [data-fc-cart-chip]'),
      function (button) {
        return !button.disabled;
      }
    );
    if (buttons.length < 2) return;

    event.preventDefault();
    var step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
    var next = buttons[(buttons.indexOf(chip) + step + buttons.length) % buttons.length];
    next.focus();
    next.click();
  });

  function handleExternalCartChange() {
    /* Our own redraw events must never feed back into a reconcile. The flag
       covers the synchronous dispatch, the cooldown covers anything the cart UI
       re-emits a moment later. */
    if (selfDispatching || Date.now() < ignoreEventsUntil) return;
    reconcile();
  }

  var boundEvents = {};
  function listenForCartChange(name) {
    if (!name || boundEvents[name]) return;
    boundEvents[name] = true;
    document.addEventListener(name, handleExternalCartChange);
  }

  ['cart:update', 'cart:refresh', 'cart:change'].forEach(listenForCartChange);

  /* Covers quick add on collection pages and anything else that goes through
     Horizon's own cart plumbing, whatever the bundle names the event. */
  import('@shopify/events')
    .then(function (module) {
      var events = module.StandardEvents || {};
      listenForCartChange(events.cartLinesUpdate);
      listenForCartChange(events.cartDiscountUpdate);
    })
    .catch(function () {
      /* The literal names above already cover the shipped bundle. */
    });

  document.addEventListener('essntl:cart:variant-updated', function (event) {
    if (event.detail && event.detail.cart) window.ESSNTL_LAST_CART = event.detail.cart;
    handleExternalCartChange();
  });

  function boot() {
    syncModules();
    hydrateCartChips();
    watchCartRows();

    /* The cart page is the one surface a customer can land on cold with a cart
       that has drifted (checkout back-button, a session started elsewhere). */
    var onCartPage =
      (window.Theme && window.Theme.template && window.Theme.template.name === 'cart') ||
      Boolean(document.querySelector('.cart-page'));

    if (onCartPage) reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.ESSNTLFreeCap = {
    enabled: true,
    config: config,
    promotions: promotions,
    reconcile: reconcile,
    setColour: setColour,
    getColour: displayColour,
    activeColour: activeColour,
    isCapLine: isCapLine,
    isEligibleLine: isEligibleLine,
    eligibleUnits: eligibleUnits,
    desiredQuantity: desiredQuantity,
    detailsHTML: detailsHTML,
    priceHTML: priceHTML,
    chipsHTML: chipsHTML,
    syncModules: syncModules,
    hydrateCartChips: hydrateCartChips,
    formatMoney: formatMoney,
    openViewer: openViewer,
    closeViewer: closeViewer,
    allowRemove: Boolean(config.allowRemove),
  };
})();